import axiosConfig from "./axiosConfig";

// Get sales/cart list with filters
export const getSales = async (filters = {}) => {
  try {
    const params = {
      status: filters.status || "",
      customer: filters.customer || "",
      creator: filters.creator || "",
      search: filters.search || "",
      start_date: filters.start_date || "",
      end_date: filters.end_date || "",
      page: filters.page || 1,
      page_size: filters.page_size || 25,
      filter_date : filters.filter_date || ""
    };

    // Remove empty parameters
    Object.keys(params).forEach((key) => {
      if (params[key] === "") {
        delete params[key];
      }
    });

    const response = await axiosConfig.get("/cart/list/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching sales:", error);
    throw error;
  }
};

// Get cart metrics from backend
export const getCartMetrics = async (filters = {}) => {
  try {
    const params = {
      status: filters.status || "",
      customer: filters.customer || "",
      creator: filters.creator || "",
      search: filters.search || "",
      start_date: filters.start_date || "",
      end_date: filters.end_date || "",
      filter_date: filters.filter_date || "",
    };

    // Remove empty parameters
    Object.keys(params).forEach((key) => {
      if (params[key] === "") {
        delete params[key];
      }
    });

    const response = await axiosConfig.get("/cart/count/", { params });
    
    // Transform backend response to match expected metrics structure
    const apiData = response.data.data;
    
    return {
      totalSales: apiData.total_sales_count?.total || 0,
      totalAmount: parseFloat(apiData.total_sales_amount?.total) || 0,
      netProfit: parseFloat(apiData.net_profit?.total) || 0,
      unpaidCustomers: apiData.unpaid_customers?.total || 0,
      salesChange: parseFloat(apiData.total_sales_count?.statistics?.change_percent) || 0,
      amountChange: parseFloat(apiData.total_sales_amount?.statistics?.change_percent) || 0,
      profitChange: parseFloat(apiData.net_profit?.statistics?.change_percent) || 0,
      unpaidChange: parseFloat(apiData.unpaid_customers?.statistics?.change_percent) || 0,
    };
  } catch (error) {
    console.error("Error fetching cart metrics:", error);
    throw error;
  }
};

// Calculate metrics from sales data locally (fallback method)
export const calculateMetrics = (salesData) => {
  if (!salesData || !Array.isArray(salesData)) {
    return {
      totalSales: 0,
      totalAmount: 0,
      netProfit: 0,
      unpaidCustomers: 0,
      salesChange: 0,
      amountChange: 0,
      profitChange: 0,
      unpaidChange: 0,
    };
  }

  const totalSales = salesData.length;
  const totalAmount = salesData.reduce(
    (sum, sale) => sum + (parseFloat(sale.total_amount) || 0),
    0
  );
  const netProfit = salesData.reduce(
    (sum, sale) => sum + (parseFloat(sale.net_profit) || 0),
    0
  );
  const unpaidCustomers = salesData.filter(
    (sale) => sale.status === "DRAFT" || sale.status === "draft"
  ).length;

  return {
    totalSales,
    totalAmount,
    netProfit,
    unpaidCustomers,
    // Mock percentage changes - you can implement actual calculation based on date ranges
    salesChange: 1.2,
    amountChange: 2.02,
    profitChange: 0.8,
    unpaidChange: -1.01,
  };
};

// Delete a sale
export const deleteSale = async (saleId) => {
  try {
    const response = await axiosConfig.delete(`/cart/delete/${saleId}/`);
    return response.data;
  } catch (error) {
    console.error("Error deleting sale:", error);
    throw error;
  }
};

// Update sale status
export const updateSaleStatus = async (saleId, status) => {
  try {
    const response = await axiosConfig.patch(`/cart/update/${saleId}/`, {
      status: status,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating sale status:", error);
    throw error;
  }
};

// Update cart with all changes
export const updateCart = async (cartId, cartData) => {
  try {
    const response = await axiosConfig.patch(
      `/cart/update/${cartId}/`,
      cartData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating cart:", error);
    throw error;
  }
};

// Get sale details by ID
export const getSaleById = async (saleId) => {
  try {
    const response = await axiosConfig.get(`/cart/detail/${saleId}/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching sale details:", error);
    throw error;
  }
};

// Publish a sale
export const publishSale = async (saleId) => {
  try {
    const response = await axiosConfig.post(`/cart/publish/${saleId}/`);
    return response.data;
  } catch (error) {
    console.error("Error publishing sale:", error);
    throw error;
  }
};

// Cancel a sale
export const cancelSale = async (saleId) => {
  try {
    const response = await axiosConfig.post(`/cart/cancel/${saleId}/`);
    return response.data;
  } catch (error) {
    console.error("Error cancelling sale:", error);
    throw error;
  }
};

// Get available customers for filter dropdown
export const getCustomers = async (search = "") => {
  try {
    const response = await axiosConfig.get("/customers/list/", {
      params: { search },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching customers:", error);
    throw error;
  }
};

// Get published sales with shipping costs (paginated, date-filtered)
export const getShippingCosts = async (filters = {}) => {
  try {
    const params = {
      filter_date: filters.filter_date || "",
      start_date: filters.start_date || "",
      end_date: filters.end_date || "",
      page: filters.page || 1,
      page_size: filters.page_size || 10,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === "") {
        delete params[key];
      }
    });

    const response = await axiosConfig.get("/cart/shipping-costs/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching shipping costs:", error);
    throw error;
  }
};

/** Export published sale as Excel/PDF for retail stock import */
export const exportSale = async (saleId, format = "xlsx") => {
  const response = await axiosConfig.get(`/cart/export/${saleId}/`, {
    params: { format },
    responseType: "blob",
  });

  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `sale_export_${saleId}.${format === "pdf" ? "pdf" : "xlsx"}`;

  const blob = new Blob([response.data], {
    type:
      format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return filename;
};

// Get InStock products list with search and pagination
export const getInStockProductsList = async (params = {}) => {
  try {
    const { search = '', page = 1, page_size = 10 } = params;
    const queryParams = new URLSearchParams();
    
    if (search) queryParams.append('search', search);
    if (page) queryParams.append('page', page);
    if (page_size) queryParams.append('page_size', page_size);
    queryParams.append('in_stock', '1');
    
    const response = await axiosConfig.get(`/products/list/?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};